<template>
<div class="vc-root" :style="themePageStyle">
  <nav class="vc-topbar"><div class="container d-flex align-items-center py-2">
    <a href="javascript:void(0)" @click="$router.go(-1)" class="vc-back"><i class="mdi mdi-arrow-left"></i></a>
    <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" class="me-3" />
    <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Consultation {{ consultation.consultation_ref }}</span>
  </div></nav>

  <div class="container vc-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!consultation.id" class="text-center py-5">
      <i class="mdi mdi-file-alert" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Consultation Not Found</h5>
    </div>
    <div v-else class="row g-4">
      <!-- Left: Detail -->
      <div class="col-lg-8">
        <!-- Status header -->
        <div class="vc-card mb-3">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div>
              <span class="vc-status" :class="'st-'+consultation.status">{{ consultation.status.replace(/_/g,' ') }}</span>
              <span class="ms-2" style="color:var(--fm-s,#6B6356);font-size:11px;">{{ consultation.consultation_type.replace(/_/g,' ') }}</span>
            </div>
            <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ consultation.consultation_ref }}</div>
          </div>

          <div class="d-flex align-items-start gap-3">
            <div>
              <div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Veterinarian</div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;">Dr. {{ consultation.vet_name }}</div>
              <div v-if="consultation.practice_name" style="color:var(--fm-m,#A09888);font-size:11px;">{{ consultation.practice_name }}</div>
            </div>
            <div style="border-left:1px solid var(--fm-br,#1E1E1E);padding-left:16px;margin-left:16px;">
              <div style="color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;">Farmer</div>
              <div style="color:var(--fm-h,#F5F0E8);font-weight:600;">{{ consultation.farmer_name }}</div>
              <div v-if="consultation.animal_name" style="color:var(--fm-m,#A09888);font-size:11px;"><i class="mdi mdi-cow me-1"></i>{{ consultation.animal_name }} {{ consultation.animal_tag?'#'+consultation.animal_tag:'' }}</div>
            </div>
          </div>
        </div>

        <!-- Reason -->
        <div class="vc-card mb-3">
          <h6 class="vc-section-title">Reason for Visit</h6>
          <div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ consultation.reason }}</div>
          <div v-if="consultation.symptoms_description" class="mt-2" style="color:var(--fm-m,#A09888);font-size:12px;">{{ consultation.symptoms_description }}</div>
          <div class="d-flex gap-3 mt-3" style="font-size:12px;color:var(--fm-s,#6B6356);">
            <span v-if="consultation.scheduled_date"><i class="mdi mdi-calendar me-1"></i>{{ consultation.scheduled_date }}</span>
            <span v-if="consultation.scheduled_time"><i class="mdi mdi-clock me-1"></i>{{ consultation.scheduled_time }}</span>
            <span v-if="consultation.species_name"><i class="mdi mdi-paw me-1"></i>{{ consultation.species_name }}</span>
          </div>
        </div>

        <!-- Diagnosis & Treatment (vet fills in) -->
        <div class="vc-card mb-3">
          <h6 class="vc-section-title">Diagnosis & Treatment</h6>
          <div v-if="consultation.status==='completed'">
            <div v-if="consultation.diagnosis" class="mb-2"><span style="color:var(--fm-s,#6B6356);font-size:11px;">Diagnosis:</span><div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ consultation.diagnosis }}</div></div>
            <div v-if="consultation.treatment_notes" class="mb-2"><span style="color:var(--fm-s,#6B6356);font-size:11px;">Treatment:</span><div style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ consultation.treatment_notes }}</div></div>
            <div v-if="consultation.follow_up_date" style="color:var(--fm-m,#A09888);font-size:12px;"><i class="mdi mdi-calendar-clock me-1"></i>Follow-up: {{ consultation.follow_up_date }}</div>
          </div>
          <div v-else-if="isVet">
            <!-- Diagnosis with suggestions -->
            <div class="mb-2 vc-suggest-field">
              <label class="vc-label">Diagnosis</label>
              <textarea v-model="diagForm.diagnosis" class="form-control vc-input" rows="2" placeholder="Start typing... e.g. mastitis, pneumonia" @input="filterSuggest('diagnosis')" @focus="activeField='diagnosis'" @blur="blurSuggest"></textarea>
              <div v-if="activeField==='diagnosis' && filteredDiag.length" class="vc-suggest-dropdown">
                <div v-for="(s,i) in filteredDiag" :key="'d'+i" class="vc-suggest-item" @mousedown.prevent="pickSuggestion('diagnosis',s)">{{ s }}</div>
              </div>
            </div>

            <!-- Treatment Notes with suggestions -->
            <div class="mb-2 vc-suggest-field">
              <label class="vc-label">Treatment Notes</label>
              <textarea v-model="diagForm.treatment_notes" class="form-control vc-input" rows="2" placeholder="Type to see common regimens..." @input="filterSuggest('treatment')" @focus="activeField='treatment'" @blur="blurSuggest"></textarea>
              <div v-if="activeField==='treatment' && filteredTreat.length" class="vc-suggest-dropdown">
                <div v-for="(s,i) in filteredTreat" :key="'t'+i" class="vc-suggest-item" @mousedown.prevent="pickSuggestion('treatment',s)">{{ s }}</div>
              </div>
            </div>

            <div class="row g-2 mb-2">
              <!-- Prescriptions with suggestions -->
              <div class="col-md-6 vc-suggest-field">
                <label class="vc-label">Prescriptions</label>
                <textarea v-model="diagForm.prescriptions" class="form-control vc-input" rows="2" placeholder="e.g. Oxytetracycline, Ivermectin..." @input="filterSuggest('rx')" @focus="activeField='rx'" @blur="blurSuggest"></textarea>
                <div v-if="activeField==='rx' && filteredRx.length" class="vc-suggest-dropdown">
                  <div v-for="(s,i) in filteredRx" :key="'r'+i" class="vc-suggest-item" @mousedown.prevent="pickSuggestion('rx',s)">{{ s }}</div>
                </div>
              </div>
              <div class="col-md-6">
                <label class="vc-label">Follow-up Date</label>
                <input v-model="diagForm.follow_up_date" class="form-control vc-input" type="date" />
              </div>
            </div>
            <button class="btn btn-warning btn-sm" @click="saveDiagnosis" :disabled="saving"><i class="mdi mdi-content-save me-1"></i>Save & Complete</button>
          </div>
          <div v-else style="color:var(--fm-s,#6B6356);font-size:12px;">Diagnosis will appear after the consultation is completed.</div>
        </div>

        <!-- Prescriptions -->
        <div v-if="consultation.prescriptions && consultation.prescriptions.length" class="vc-card mb-3">
          <h6 class="vc-section-title">Prescriptions</h6>
          <div v-for="(p,pi) in consultation.prescriptions" :key="pi" class="vc-rx-item">
            <div style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ p.name || p }}</div>
            <div v-if="p.dosage" style="color:var(--fm-m,#A09888);font-size:11px;">{{ p.dosage }} · {{ p.frequency }}</div>
          </div>
        </div>

        <!-- Chat Messages -->
        <div class="vc-card">
          <h6 class="vc-section-title">Messages</h6>
          <div class="vc-chat">
            <div v-if="!messages.length" class="text-center py-3" style="color:var(--fm-s,#6B6356);font-size:12px;">No messages yet</div>
            <div v-for="m in messages" :key="m.id" class="vc-msg" :class="{'vc-msg-mine':m.sender_id===userId}">
              <div class="vc-msg-name">{{ m.sender_name }} · {{ formatTime(m.created_at) }}</div>
              <div class="vc-msg-text">{{ m.content }}</div>
            </div>
          </div>
          <div class="d-flex gap-2 mt-2">
            <input v-model="newMessage" class="form-control vc-input flex-fill" placeholder="Type a message..." @keyup.enter="sendMessage" style="font-size:12px;" />
            <button class="btn btn-warning btn-sm" @click="sendMessage" :disabled="!newMessage.trim()||sendingMsg"><i class="mdi mdi-send"></i></button>
          </div>
        </div>
      </div>

      <!-- Right: Summary -->
      <div class="col-lg-4">
        <div class="vc-card mb-3" style="position:sticky;top:80px;">
          <h6 class="vc-section-title">Payment</h6>
          <div class="d-flex justify-content-between mb-1"><span style="color:var(--fm-s,#6B6356);font-size:12px;">Consultation Fee</span><span style="color:var(--fm-h,#F5F0E8);font-size:13px;">KES {{ fNum(consultation.fee) }}</span></div>
          <div v-if="consultation.additional_charges>0" class="d-flex justify-content-between mb-1"><span style="color:var(--fm-s,#6B6356);font-size:12px;">Additional Charges</span><span style="color:var(--fm-h,#F5F0E8);font-size:13px;">KES {{ fNum(consultation.additional_charges) }}</span></div>
          <div class="d-flex justify-content-between pt-2 mt-2" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <span style="color:var(--fm-h,#F5F0E8);font-weight:700;">Total</span>
            <span style="color:#BFA164;font-weight:700;font-size:16px;">KES {{ fNum(consultation.total_charge||consultation.fee) }}</span>
          </div>
          <div class="mt-2"><span class="vc-status" :class="'pst-'+consultation.payment_status">{{ consultation.payment_status }}</span></div>

          <!-- Vet actions -->
          <div v-if="isVet && consultation.status==='requested'" class="mt-3 pt-3" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <div v-if="prepaymentBlocked" style="background:rgba(255,152,0,.08);border:1px solid rgba(255,152,0,.3);color:#FF9800;padding:10px 12px;border-radius:6px;font-size:12px;margin-bottom:8px;">
              <i class="mdi mdi-cash-clock me-1"></i><strong>Awaiting farmer payment.</strong> Your profile requires upfront payment before accepting. The farmer has been asked to pay KES {{ fNum(consultation.fee) }}.
            </div>
            <button class="btn btn-success btn-sm w-100 mb-2" @click="updateStatus('confirmed')" :disabled="prepaymentBlocked"><i class="mdi mdi-check me-1"></i>Accept Appointment</button>
            <button class="btn btn-outline-danger btn-sm w-100" @click="updateStatus('cancelled')"><i class="mdi mdi-close me-1"></i>Decline</button>
          </div>
          <div v-if="isVet && consultation.status==='confirmed'" class="mt-3 pt-3" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <button class="btn btn-warning btn-sm w-100" @click="updateStatus('in_progress')"><i class="mdi mdi-play me-1"></i>Start Consultation</button>
          </div>

          <!-- Farmer rating -->
          <div v-if="!isVet && consultation.status==='completed' && !consultation.farmer_rating" class="mt-3 pt-3" style="border-top:1px solid var(--fm-br,#1E1E1E);">
            <h6 class="vc-section-title">Rate this Consultation</h6>
            <div class="d-flex gap-1 mb-2">
              <i v-for="s in 5" :key="s" class="mdi" :class="s<=ratingForm.rating?'mdi-star':'mdi-star-outline'" style="font-size:24px;cursor:pointer;" :style="{color:s<=ratingForm.rating?'#FFD700':'var(--fm-s,#3A3530)'}" @click="ratingForm.rating=s"></i>
            </div>
            <textarea v-model="ratingForm.review" class="form-control vc-input mb-2" rows="2" placeholder="Write a review..." style="font-size:12px;"></textarea>
            <button class="btn btn-warning btn-sm w-100" @click="submitRating" :disabled="!ratingForm.rating">Submit Rating</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] .vc-root{background:#F7F5F2!important}
html[data-theme="light"] .vc-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .vc-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .vc-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .vc-msg{background:#F0EBE4!important}
html[data-theme="light"] .vc-msg-mine{background:rgba(191,161,100,.08)!important}
html[data-theme="light"] .vc-chat{background:#F7F5F2!important;border-color:#E8E2DA!important}
</style>

<style scoped>
.vc-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh}
.vc-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.vc-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:12px;text-decoration:none}
.vc-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.vc-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px}
.vc-section-title{color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px}
.vc-status{font-size:10px;font-weight:600;padding:3px 10px;border-radius:8px;text-transform:capitalize}
.st-requested{background:rgba(255,152,0,.1);color:#FF9800}
.st-confirmed{background:rgba(79,195,247,.1);color:#4FC3F7}
.st-en_route{background:rgba(191,161,100,.1);color:#BFA164}
.st-in_progress{background:rgba(191,161,100,.15);color:#BFA164}
.st-completed{background:rgba(80,200,120,.1);color:#50C878}
.st-cancelled{background:rgba(255,107,107,.1);color:#FF6B6B}
.pst-pending{background:rgba(255,152,0,.1);color:#FF9800}
.pst-paid{background:rgba(80,200,120,.1);color:#50C878}
.vc-label{display:block;color:var(--fm-s,#6B6356);font-size:11px;font-weight:500;margin-bottom:3px}
.vc-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.vc-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.vc-rx-item{padding:8px 0;border-bottom:1px solid var(--fm-br,#1A1A1A)}.vc-rx-item:last-child{border-bottom:none}
.vc-suggest-field{position:relative}
.vc-suggest-dropdown{position:absolute;left:0;right:0;top:calc(100% - 2px);background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:6px;max-height:220px;overflow-y:auto;z-index:20;box-shadow:0 8px 20px rgba(0,0,0,.4)}
.vc-suggest-item{padding:8px 12px;font-size:12px;color:var(--fm-h,#F5F0E8);cursor:pointer;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.vc-suggest-item:last-child{border-bottom:none}
.vc-suggest-item:hover{background:rgba(191,161,100,.08);color:#BFA164}
.vc-chat{max-height:300px;overflow-y:auto;background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:10px}
.vc-msg{background:var(--fm-bg3,#1E1E1E);border-radius:8px;padding:8px 12px;margin-bottom:6px;max-width:80%}
.vc-msg-mine{margin-left:auto;background:rgba(191,161,100,.1)}
.vc-msg-name{color:var(--fm-s,#6B6356);font-size:9px;margin-bottom:2px}
.vc-msg-text{color:var(--fm-h,#F5F0E8);font-size:12px}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .vc-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .vc-card { padding: 12px !important; border-radius: 10px !important; }
  .vc-msg { max-width: 88%; padding: 7px 11px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "VetConsultation",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,
    consultation:{},messages:[],
    userId:sessionStorage.getItem('userId')||'',
    diagForm:{diagnosis:'',treatment_notes:'',prescriptions:'',follow_up_date:''},
    ratingForm:{rating:0,review:''},
    newMessage:'',sendingMsg:false,saving:false,
    activeField:null,filteredDiag:[],filteredTreat:[],filteredRx:[],blurTimer:null,
    diagnosisOptions:[
      'Mastitis','East Coast Fever','Foot and Mouth Disease','Anaplasmosis','Babesiosis',
      'Brucellosis','Contagious Bovine Pleuropneumonia (CBPP)','Dermatophilosis','Helminthiasis (worms)',
      'Heat stress','Ketosis','Lumpy Skin Disease','Milk fever','Newcastle disease','Gumboro (IBD)',
      'Coccidiosis','Pneumonia','Respiratory infection','Rumen acidosis','Tick infestation',
      'Trypanosomiasis','Bloat','Diarrhea','Skin infection (pyoderma)','Eye infection / conjunctivitis',
      'Lameness','Fever of unknown origin','Mange','Retained placenta','Abortion (infectious)',
      'Endometritis','Hypocalcemia','Hypomagnesemia','Fascioliasis (liver flukes)','Rift Valley Fever',
      'Fowl Typhoid','Fowl Cholera','Marek\'s Disease','Infectious Bronchitis','Colibacillosis'
    ],
    treatmentOptions:[
      'Oxytetracycline 20% LA, 1ml/10kg IM, single dose',
      'Penicillin-Streptomycin, 1ml/25kg IM, once daily for 3 days',
      'Albendazole 10% drench, 7.5mg/kg PO, single dose',
      'Ivermectin 1% SC, 0.2mg/kg, repeat in 14 days',
      'Diminazene aceturate (Berenil) 3.5mg/kg IM, single dose',
      'Dexamethasone 0.1mg/kg IM for inflammation',
      'Calcium borogluconate 300ml IV, slow',
      'NSAID (Flunixin meglumine) 1.1mg/kg IV',
      'Amitraz 12.5% dip/spray, weekly for 3 weeks',
      'Intramammary antibiotic tube, 1 per quarter, 2x daily for 3 days',
      'Oral rehydration salts + IV Ringer\'s lactate',
      'Enrofloxacin 10%, 5mg/kg SC, once daily for 5 days',
      'Sulphadimidine 33%, 100ml per 250kg IV',
      'Vitamin B-complex 10ml IM',
      'Multivitamin + iron injection 10ml IM',
      'Oxytocin 10IU, 2ml IM (post-partum)',
      'Tylosin 20%, 0.1ml/kg IM for 3 days',
      'Supportive care, isolation, and rest',
      'Deworming followed by mineral supplementation',
      'Antibiotic drench + probiotic paste'
    ],
    prescriptionOptions:[
      'Oxytetracycline 20% LA — 1ml/10kg IM, repeat in 72hrs',
      'Penicillin-Streptomycin — 1ml/25kg IM x 3 days',
      'Albendazole 10% drench — 7.5mg/kg PO',
      'Ivermectin 1% SC — 0.2mg/kg',
      'Diminazene aceturate (Berenil) — 3.5mg/kg IM',
      'Dexamethasone — 0.1mg/kg IM',
      'Calcium borogluconate 300ml — IV slow',
      'Flunixin meglumine — 1.1mg/kg IV or IM',
      'Amitraz 12.5% spray — weekly x 3',
      'Ceftiofur intramammary tube — 1 per quarter, 2x daily x 3',
      'Multivitamin injection — 10ml IM',
      'Cefquinome 4% — 1ml/50kg IM x 3 days',
      'Enrofloxacin 10% — 5mg/kg SC',
      'Sulphadimidine 33% — 100ml/250kg IV',
      'Vitamin B-complex — 10ml IM',
      'Oxytocin 10IU — 2ml IM',
      'Tylosin 20% — 0.1ml/kg IM',
      'Glucose 5% IV drip — 1–2L',
      'Atropine sulphate — 0.05mg/kg IV',
      'Tramadol 50mg — 1mg/kg IM for pain',
      'Levamisole — 7.5mg/kg PO',
      'Closantel — 10mg/kg PO for fluke',
      'Tetracycline eye ointment — apply 2–3x daily x 5 days'
    ]
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    isVet(){return this.consultation.vet_user_id===this.userId},
    prepaymentBlocked(){
      return String(this.consultation.requires_prepayment||'0')==='1' && (this.consultation.payment_status||'pending')!=='paid';
    }
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';
    this.syncTheme();this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.loadConsultation();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect();if(this.blurTimer)clearTimeout(this.blurTimer);},
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    fNum(n){return Number(n||0).toLocaleString()},
    formatTime(d){if(!d)return'';var dt=new Date(d);return dt.toLocaleDateString('en-KE',{day:'numeric',month:'short'})+' '+dt.toLocaleTimeString('en-KE',{hour:'2-digit',minute:'2-digit'})},
    async loadConsultation(){
      this.loading=true;
      var id=this.$route.params.consultationId||this.$route.params.id;
      try{
        var r=await executeJson({function:'getConsultationDetail',consultationId:id},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.consultation=r.data.data.consultation||{};
          this.messages=r.data.data.messages||[];
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async updateStatus(status){
      try{
        var actorUserId=sessionStorage.getItem('userId')||'';
        var payload={function:'updateConsultation',consultationId:this.consultation.id,status:status,actorUserId:actorUserId};
        if(status==='cancelled'){
          var reason=prompt('Reason for cancellation (optional):','');
          if(reason===null)return; // user hit Cancel on the prompt
          if(reason)payload.cancellationReason=reason;
        }
        await executeJson(payload,flocker);
        this.loadConsultation();
      }catch(e){alert('Error')}
    },
    async saveDiagnosis(){
      this.saving=true;
      try{
        var actorUserId=sessionStorage.getItem('userId')||'';
        await executeJson({function:'updateConsultation',consultationId:this.consultation.id,actorUserId:actorUserId,
          status:'completed',diagnosis:this.diagForm.diagnosis,treatmentNotes:this.diagForm.treatment_notes,
          prescriptions:this.diagForm.prescriptions,followUpDate:this.diagForm.follow_up_date},flocker);
        this.loadConsultation();
      }catch(e){alert('Error saving')}
      this.saving=false;
    },
    currentLine(field){
      var txt=field==='diagnosis'?this.diagForm.diagnosis:(field==='treatment'?this.diagForm.treatment_notes:this.diagForm.prescriptions);
      var lastNl=String(txt||'').lastIndexOf('\n');
      return lastNl>=0 ? txt.substring(lastNl+1) : (txt||'');
    },
    filterSuggest(field){
      var q=this.currentLine(field).trim().toLowerCase();
      var src=field==='diagnosis'?this.diagnosisOptions:(field==='treatment'?this.treatmentOptions:this.prescriptionOptions);
      var matches;
      if(q.length===0){matches=src.slice(0,8);}
      else{matches=src.filter(function(s){return s.toLowerCase().indexOf(q)>=0;}).slice(0,10);}
      if(field==='diagnosis')this.filteredDiag=matches;
      else if(field==='treatment')this.filteredTreat=matches;
      else this.filteredRx=matches;
    },
    pickSuggestion(field,value){
      var keys={diagnosis:'diagnosis',treatment:'treatment_notes',rx:'prescriptions'};
      var key=keys[field];
      var txt=this.diagForm[key]||'';
      var lastNl=txt.lastIndexOf('\n');
      var base=lastNl>=0?txt.substring(0,lastNl+1):'';
      var currentPartial=lastNl>=0?txt.substring(lastNl+1):txt;
      // If user was typing a partial match, replace it; else append on a new line
      this.diagForm[key] = currentPartial.trim() ? (base+value+'\n') : (txt + (txt && !txt.endsWith('\n')?'\n':'') + value + '\n');
      // refresh suggestions for current field so list stays relevant
      this.filterSuggest(field);
    },
    blurSuggest(){
      var self=this;
      if(this.blurTimer)clearTimeout(this.blurTimer);
      this.blurTimer=setTimeout(function(){self.activeField=null;},150);
    },
    async sendMessage(){
      if(!this.newMessage.trim())return;
      this.sendingMsg=true;
      try{
        await executeJson({function:'sendConsultationMessage',consultationId:this.consultation.id,senderId:this.userId,content:this.newMessage},flocker);
        this.newMessage='';this.loadConsultation();
      }catch(e){alert('Error sending')}
      this.sendingMsg=false;
    },
    async submitRating(){
      try{
        await executeJson({function:'rateConsultation',consultationId:this.consultation.id,rating:this.ratingForm.rating,review:this.ratingForm.review},flocker);
        this.loadConsultation();
      }catch(e){alert('Error')}
    }
  }
};
</script>
